import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FormMultiSelect from 'shared/components/forms/FormMultiSelect';
import OpeningHoursPicker from 'features/onboarding/components/forms/OpeningHoursPicker';
import { hoursScheduleSchema } from 'features/onboarding/schemas';
import { cn } from 'shared/lib/utils';
import {
  DAYS,
  type DayKey,
  type OpeningHours,
} from 'features/onboarding/types/onboarding.types';

interface ReviewHoursCardProps {
  workingDays: DayKey[];
  openingHours: OpeningHours;
  onWorkingDaysChange: (days: DayKey[], openingHours: OpeningHours) => void;
  onOpeningHoursChange: (openingHours: OpeningHours) => void;
  /** When true, run validation and surface field errors. */
  showErrors?: boolean;
}

/**
 * Working days + opening hours, filled on the review ("almost done") step.
 * Selecting days drives which rows the hours picker shows and marks every
 * other day as closed.
 */
const ReviewHoursCard: React.FC<ReviewHoursCardProps> = ({
  workingDays,
  openingHours,
  onWorkingDaysChange,
  onOpeningHoursChange,
  showErrors,
}) => {
  const { t } = useTranslation();
  const [touched, setTouched] = useState(false);

  const dayOptions = useMemo(
    () =>
      DAYS.map((day) => ({
        value: day,
        label: t(`openingHours.days.${day}`) as string,
      })),
    [t],
  );

  const errors = useMemo(() => {
    if (!showErrors && !touched) return {};
    const result = hoursScheduleSchema.safeParse({ workingDays, openingHours });
    if (result.success) return {};
    const next: Record<string, string | undefined> = {};
    result.error.issues.forEach((issue) => {
      const key = issue.path[0];
      if (typeof key === 'string' && !next[key]) next[key] = issue.message;
    });
    return next;
  }, [workingDays, openingHours, showErrors, touched]);

  const applyWorkingDays = (next: string[]) => {
    setTouched(true);
    const days = DAYS.filter((d) => next.includes(d));
    const synced: OpeningHours = { ...openingHours };
    DAYS.forEach((d) => {
      synced[d] = { ...synced[d], enabled: days.includes(d) };
    });
    onWorkingDaysChange(days, synced);
  };

  return (
    <div
      id="review-section-hours"
      className={cn(
        'card scroll-mt-24 p-5 sm:p-6 flex flex-col gap-6',
        showErrors && Object.keys(errors).length > 0 && 'border-red-200',
      )}
    >
      <div>
        <h3 className="text-base font-semibold text-gray-900">
          {t('review.sectionHours')}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {t('review.sectionHoursHint')}
        </p>
      </div>

      <FormMultiSelect
        label={t('workingDays.label') as string}
        placeholder={t('workingDays.placeholder') as string}
        options={dayOptions}
        value={workingDays}
        onChange={applyWorkingDays}
        error={errors.workingDays}
        required
      />

      <div className="flex flex-col gap-2">
        <span className="text-base font-medium text-gray-700">
          {t('businessInfo.openingHours')}
          <span className="text-gray-500 ms-1">*</span>
        </span>
        <OpeningHoursPicker
          value={openingHours}
          days={workingDays}
          onChange={(next) => {
            setTouched(true);
            onOpeningHoursChange(next);
          }}
          error={errors.openingHours}
        />
      </div>
    </div>
  );
};

export default ReviewHoursCard;
